import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, Loader2, RefreshCw, ShieldX, Smartphone, TimerOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
    IdentityApiError,
    type ApprovalStart,
    type ApprovalStatusResponse,
    type SessionResponse
} from "@/api/identityApi";

/** Poll every 2 s (Mobile Phase 1 contract §2); requests live 90 s. */
const POLL_MS = 2000;
const LIFETIME_MS = 90_000;

type Phase = "starting" | "pending" | "approved" | "denied" | "expired" | "error";

export interface PhoneApprovalProps {
    title: string;
    description: ReactNode;
    /** Creates the request (sets the browser-binding cookie). Called on mount and on "Send again". */
    start: () => Promise<ApprovalStart>;
    poll: (challengeId: string) => Promise<ApprovalStatusResponse>;
    /** The server already completed the sign-in; just move on. */
    onApproved: (session: SessionResponse) => void;
    /** Friendly text for a failed start; return null for the default. */
    describeStartError?: (err: unknown) => string | null;
    /** Extra help under "The request expired". */
    expiredHint?: ReactNode;
    /** Footer links (e.g. "Use a verification code instead"). */
    children?: ReactNode;
}

/**
 * "Approve on your phone": shows the two-digit number to tap in Athavita Authenticator, a 90 s countdown,
 * and polls until the phone approves, denies, or the request expires — with a way to send a new one.
 */
export function PhoneApproval({
    title,
    description,
    start,
    poll,
    onApproved,
    describeStartError,
    expiredHint,
    children
}: PhoneApprovalProps) {
    const [challenge, setChallenge] = useState<ApprovalStart | null>(null);
    const [phase, setPhase] = useState<Phase>("starting");
    const [error, setError] = useState<string | null>(null);
    const [deadline, setDeadline] = useState(0);
    const [now, setNow] = useState(() => Date.now());

    // Latest callbacks without re-running effects; `generation` drops answers for a superseded request.
    const startRef = useRef(start);
    const pollRef = useRef(poll);
    const approvedRef = useRef(onApproved);
    const describeRef = useRef(describeStartError);
    startRef.current = start;
    pollRef.current = poll;
    approvedRef.current = onApproved;
    describeRef.current = describeStartError;
    const generation = useRef(0);

    const begin = useCallback(async () => {
        const gen = ++generation.current;
        setPhase("starting");
        setError(null);
        setChallenge(null);
        try {
            const next = await startRef.current();
            if (gen !== generation.current) return;
            const remaining = Date.parse(next.expiresAt) - Date.now();
            // Trust the server's expiry unless this computer's clock clearly disagrees with it.
            const lifetime = remaining > 1000 && remaining <= LIFETIME_MS + 5000 ? remaining : LIFETIME_MS;
            setDeadline(Date.now() + lifetime);
            setNow(Date.now());
            setChallenge(next);
            setPhase("pending");
        } catch (err) {
            if (gen !== generation.current) return;
            setError(describeRef.current?.(err) ?? defaultStartError(err));
            setPhase("error");
        }
    }, []);

    useEffect(() => {
        void begin();
        return () => {
            generation.current += 1;
        };
    }, [begin]);

    // Poll while pending.
    useEffect(() => {
        if (phase !== "pending" || !challenge) return;
        const gen = generation.current;
        let stopped = false;
        let failures = 0;
        let timer: number | undefined;

        const tick = async () => {
            try {
                const result = await pollRef.current(challenge.challengeId);
                if (stopped || gen !== generation.current) return;
                failures = 0;
                if (result.status === "approved" && result.session) {
                    setPhase("approved");
                    approvedRef.current(result.session);
                    return;
                }
                if (result.status === "denied" || result.status === "expired") {
                    setPhase(result.status);
                    return;
                }
            } catch (err) {
                if (stopped || gen !== generation.current) return;
                // 404 = this browser no longer holds the request (e.g. cookie gone) — same as expired.
                if (err instanceof IdentityApiError && err.status === 404) {
                    setPhase("expired");
                    return;
                }
                failures += 1;
                if (failures >= 5) {
                    setError("We lost touch with the sign-in service. Check your connection and try again.");
                    setPhase("error");
                    return;
                }
            }
            if (Date.now() > deadline + 10_000) {
                setPhase("expired");
                return;
            }
            timer = window.setTimeout(() => void tick(), POLL_MS);
        };

        timer = window.setTimeout(() => void tick(), POLL_MS);
        return () => {
            stopped = true;
            if (timer !== undefined) window.clearTimeout(timer);
        };
    }, [phase, challenge, deadline]);

    // Countdown.
    useEffect(() => {
        if (phase !== "pending") return;
        const id = window.setInterval(() => setNow(Date.now()), 250);
        return () => window.clearInterval(id);
    }, [phase]);

    const msLeft = Math.max(0, deadline - now);
    const secondsLeft = Math.ceil(msLeft / 1000);
    const fraction = Math.min(1, msLeft / LIFETIME_MS);

    return (
        <Card className="w-full">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Smartphone className="h-5 w-5 text-primary" aria-hidden /> {title}
                </CardTitle>
                <CardDescription>{description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
                {phase === "starting" ? (
                    <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Sending a request to your phone…
                    </div>
                ) : null}

                {(phase === "pending" || phase === "approved") && challenge ? (
                    <div className="flex flex-col items-center gap-4 py-2">
                        <p className="text-sm text-muted-foreground">Tap this number in Athavita Authenticator</p>
                        <div
                            aria-live="polite"
                            aria-label={`Number to tap: ${challenge.number}`}
                            className="flex h-28 w-28 select-none items-center justify-center rounded-2xl border-2 border-primary/30 bg-primary/5 text-6xl font-bold tabular-nums tracking-wider text-primary"
                        >
                            {challenge.number}
                        </div>
                        {phase === "approved" ? (
                            <p className="flex items-center gap-2 text-sm font-medium text-foreground">
                                <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden /> Approved — signing you in…
                            </p>
                        ) : (
                            <div className="w-full max-w-xs space-y-1.5">
                                <div
                                    className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
                                    role="progressbar"
                                    aria-label="Time left to approve"
                                    aria-valuemin={0}
                                    aria-valuemax={Math.round(LIFETIME_MS / 1000)}
                                    aria-valuenow={secondsLeft}
                                >
                                    <div
                                        className="h-full rounded-full bg-primary transition-[width] duration-300 ease-linear"
                                        style={{ width: `${fraction * 100}%` }}
                                    />
                                </div>
                                <p className="text-center text-xs text-muted-foreground">
                                    {secondsLeft > 0 ? `Expires in ${formatSeconds(secondsLeft)}` : "Checking one last time…"}
                                </p>
                            </div>
                        )}
                    </div>
                ) : null}

                {phase === "denied" ? (
                    <Alert variant="destructive">
                        <ShieldX className="h-4 w-4" />
                        <AlertTitle>Sign-in was denied</AlertTitle>
                        <AlertDescription>
                            The request was declined on your phone, or a different number was tapped. If you didn't
                            start this sign-in, change your password.
                        </AlertDescription>
                    </Alert>
                ) : null}

                {phase === "expired" ? (
                    <Alert>
                        <TimerOff className="h-4 w-4" />
                        <AlertTitle>The request expired</AlertTitle>
                        <AlertDescription>
                            Nothing was approved in time. Send a new request to try again.
                            {expiredHint ? <span className="mt-1 block">{expiredHint}</span> : null}
                        </AlertDescription>
                    </Alert>
                ) : null}

                {phase === "error" && error ? (
                    <Alert variant="destructive">
                        <ShieldX className="h-4 w-4" />
                        <AlertTitle>Couldn't reach your phone</AlertTitle>
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                ) : null}

                {phase !== "starting" && phase !== "approved" ? (
                    <Button
                        type="button"
                        variant={phase === "pending" ? "outline" : "default"}
                        className="w-full"
                        onClick={() => void begin()}
                    >
                        <RefreshCw className="h-4 w-4" aria-hidden />
                        {phase === "pending" ? "Send again" : "Send a new request"}
                    </Button>
                ) : null}

                {children ? (
                    <div className="flex flex-wrap items-center justify-between gap-2 text-sm">{children}</div>
                ) : null}
            </CardContent>
        </Card>
    );
}

function formatSeconds(total: number): string {
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

function defaultStartError(err: unknown): string {
    if (err instanceof IdentityApiError) {
        if (err.status === 429) return "Too many sign-in requests. Wait a minute, then try again.";
        return err.detail ?? "The request couldn't be sent. Try again.";
    }
    return "Couldn't reach the sign-in service. Check your connection and try again.";
}
